import type { PartDefinition } from "@rogue/rackbuilder"
import { AdjustOnlyVisual } from "@/js/pages/product/rackbuilder/constants"
import { getRackModel } from "@/js/pages/product/rackbuilder/modelPaths"
import { getFlipX, getFlipZ, buildTopWeightStackHardware, buildWeightStack300Plates } from "../fmlSharedParts"
import type { FMLContext } from "../fmlSharedParts"

// Twin FML Parts — All part definitions for FML-6-Twin and FML-HR-Twin

// TwinFMLContext is identical to FMLContext — re-export as alias for backward compatibility
export type TwinFMLContext = FMLContext

// MARK: Side Meshes (full rack — back crossmember slot)

/**
 * Twin weight stack assembly — goes into backCrossmember.slots.fml for full rack.
 * Includes poles, cables, and weight plate hardware.
 */
export function createTwinFMLWeightStack(context: TwinFMLContext, left: boolean): PartDefinition {
  const { config, materials } = context

  const twinWeightStackCable: PartDefinition = {
    mesh  : getRackModel('FML_CABLES', 'weight stack cable'),
    materials,
    slots : {},
  }

  const bottomWeightStackCable: PartDefinition = {
    mesh  : getRackModel('FML_CABLES', `weight stack pulley cable ${config.weightStackAmount}`),
    slots : {},
  }

  const twinPoles: PartDefinition = {
    mesh  : getRackModel('FML_TWIN', 'stack poles'),
    materials,
    slots : {
      cable  : twinWeightStackCable,
      bottom : bottomWeightStackCable,
    },
    anchor : 'bottom',
    offset : { y: -0.7 },
  }

  const weightStack: PartDefinition = {
    mesh        : getRackModel('FM_SHARED', 'weight stack'),
    materials,
    adjustments : getFlipX(left),
    offset      : { z: -0.5, y: 0.3 },
    slots       : { pole: twinPoles },
  }

  if (config.weightStackAmount === '300') {
    weightStack.slots.weights_300 = [buildTopWeightStackHardware(), buildWeightStack300Plates()]
  } else {
    weightStack.slots.weights_250 = [buildTopWeightStackHardware()]
  }

  return weightStack
}

/**
 * Twin side shrouds — full rack only. Different mesh from standard side shrouds.
 */
export function createTwinFMLSideShrouds(left: boolean): PartDefinition {
  return {
    mesh   : getRackModel('FML_TWIN', 'shroud 90'),
    slots  : {},
    anchor : 'right',
    offset : left ? { y: 3, z: 3.7 } : { y: 3, z: 3.7, x: 0.3 },
  }
}

/**
 * Full rack side meshes: [twinWeightStack, ?twinSideShrouds]
 */
export function createTwinFMLSideMeshes(context: TwinFMLContext, left: boolean): PartDefinition[] {
  const parts: PartDefinition[] = [createTwinFMLWeightStack(context, left)]

  if (context.config.ftShrouds) {
    parts.push(createTwinFMLSideShrouds(left))
  }

  return parts
}

// MARK: Front System (full rack — bottomCrossmember.slots.fml)

/**
 * Twin front crossmember pulley system — replaces the standard FML front + sharedRails.
 * Contains top_rail, bottom_rail, and cable slots.
 */
export function createTwinFMLFrontSystem(context: TwinFMLContext, left: boolean): PartDefinition {
  const { config, materials, depth } = context

  const topCable: PartDefinition = {
    mesh  : getRackModel('FML_CABLES', 'front cable'),
    materials,
    slots : {},
  }

  const bottomCable: PartDefinition = {
    mesh  : getRackModel('FML_CABLES', 'bottom front cable'),
    materials,
    slots : {},
  }

  const twinFrontCrossmemberHardware: PartDefinition = {
    mesh   : getRackModel('FML_TWIN', 'front crossmember pulley hardware'),
    slots  : {},
    anchor : 'right',
    materials,
  }

  const depthOffset = -(Number(depth) - 3) // FP: Number(-depth + 3)

  // Rail bottom — anchor right, flipped on the right side with depth-based offset
  const twinRailBottom: PartDefinition = {
    mesh        : getRackModel('FML_TWIN', `bottom rail ${depth}`),
    slots       : {},
    anchor      : 'right',
    materials,
    adjustments : getFlipZ(left),
    offset      : left ? {} : { z: depthOffset },
  }

  // Rail top — same pattern as bottom with a small x-offset on right side
  const twinRailTop: PartDefinition = {
    mesh        : getRackModel('FML_TWIN', `top rail ${depth}`),
    slots       : {},
    anchor      : 'right',
    materials,
    adjustments : getFlipZ(left),
    offset      : left ? {} : { z: depthOffset, x: -0.2 },
  }

  return {
    mesh  : getRackModel('FML_TWIN', 'front crossmember pulleys'),
    slots : {
      top_rail                     : twinRailTop,
      bottom_rail                  : twinRailBottom,
      top_cable                    : topCable,
      bottom_cable                 : bottomCable,
      [`hardware-${config.depth}`] : twinFrontCrossmemberHardware,
    },
    anchor      : 'bottom',
    materials,
    adjustments : getFlipX(left),
    offset      : left ? { z: -3.6, x: -0.5 } : { z: -3.6, x: 0.5 },
  }
}

// MARK: Extra Front Meshes (full rack — appended after front system)

/**
 * Top rail pulley — absolute-positioned relative to the bottom crossmember.
 */
export function createTwinFMLTopRailPulley(context: TwinFMLContext, left: boolean): PartDefinition {
  const { materials, depth } = context
  const depthNum = Number(depth)

  return {
    mesh           : getRackModel('FML_TWIN', 'top rail pulley'),
    materials,
    slots          : {},
    anchor         : 'top',
    adjustments    : getFlipX(left),
    adjustmentType : AdjustOnlyVisual,
    offset         : left
      ? { x: -2.6, y: 83.5, z: -(depthNum - 0.2) }
      : { x: 0.9, y: 83.5, z: -(depthNum - 0.2) },
  }
}

/**
 * Bottom rail pulley with bottom back cable — absolute-positioned.
 */
export function createTwinFMLBottomRailPulley(context: TwinFMLContext, left: boolean): PartDefinition {
  const { materials, depth } = context
  const depthNum = Number(depth)

  const bottomBackCable: PartDefinition = {
    mesh           : getRackModel('FML_CABLES', 'bottom back cable'),
    materials,
    slots          : {},
    adjustments    : getFlipX(left),
    adjustmentType : AdjustOnlyVisual,
    offset         : { y: 0.5 },
  }

  return {
    mesh           : getRackModel('FML_TWIN', 'bottom rail pulley'),
    materials,
    slots          : { cable: bottomBackCable },
    anchor         : 'bottom',
    adjustments    : getFlipX(left),
    adjustmentType : AdjustOnlyVisual,
    offset         : left
      ? { x: -2.5, y: -1.2, z: -(depthNum - 5) }
      : { x: 3, y: -1.2, z: -(depthNum - 5) },
  }
}

// MARK: Back Post (for back stabilizer middle slot)

/**
 * Twin back post — holds twinTopPulley (with pullup) and optional back shroud.
 * Goes into backStabilizer.slots.middle alongside lowRow.
 */
export function createTwinFMLBackPost(
  context: TwinFMLContext,
  pullup: PartDefinition
): PartDefinition {
  const { config, materials } = context

  const twinTopPulley: PartDefinition = {
    mesh   : getRackModel('FML_TWIN', 'back post top'),
    slots  : { front: pullup },
    anchor : 'back',
    materials,
  }

  const twinBackShroud: PartDefinition = {
    mesh   : getRackModel('FML_TWIN', 'back shroud'),
    slots  : {},
    anchor : 'bottom',
  }

  return {
    mesh  : getRackModel('FML_TWIN', 'back post'),
    slots : config.ftShrouds
      ? { top: twinTopPulley, back_shroud: twinBackShroud }
      : { top: twinTopPulley },
    anchor : 'right',
    materials,
    offset : { z: -1.5 },
  }
}
